<!--
  @description 基于 Element UI Dialog 的二次封装对话框组件
  @author [your name]
  @example
  <rhcn-dialog
    :visible.sync="dialogVisible"
    title="标题"
    @confirm="handleConfirm"
    @cancel="handleCancel"
  >
    对话框内容
  </rhcn-dialog>
-->
<template>
  <div class="rhcn-dialog-wrapper">
    <!-- 使用 Element UI Dialog 组件，并透传未声明的属性 -->
    <el-dialog
      v-bind="$attrs"
      :visible.sync="dialogVisible"
      :show-close="false"
      :before-close="handleClose"
      @open="$emit('open')"
      @opened="$emit('opened')"
      @close="$emit('close')"
      @closed="$emit('closed')"
    >
      <!-- 自定义标题插槽 -->
      <template v-if="$slots.title" #title>
        <slot name="title"></slot>
      </template>

      <!-- 默认标题样式 -->
      <template v-else-if="customTitle" #title>
        <div class="rhcn-dialog-title">
          <div class="title-text">{{ title }}</div>
          <img v-if="showClose" src="@/assets/image/icon-close2x.png" alt="close" class="rhcn-dialog-close" @click="handleClose">
        </div>
      </template>

      <!-- 默认内容插槽 -->
      <slot></slot>

      <!-- 自定义底部插槽 -->
      <template v-if="$slots.footer" #footer>
        <slot name="footer"></slot>
      </template>

      <!-- 默认底部按钮 -->
      <template v-else-if="showFooter" #footer>
        <div class="rhcn-dialog-footer" :class="{ 'is-right': footerAlign === 'right' }">
          <rhcn-button
            v-if="showCancelButton"
            type="info"
            plain
            @click="handleCancel"
            :disabled="cancelDisabled"
          >
            {{ cancelText }}
          </rhcn-button>
          <rhcn-button
            v-if="showConfirmButton"
            @click="handleConfirm"
            :disabled="confirmDisabled"
          >
            {{ confirmText }}
          </rhcn-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'RhcnDialog',
  inheritAttrs: false,
  props: {
    /**
     * 控制对话框是否可见
     * @model
     */
    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * 对话框标题
     */
    title: {
      type: String,
      default: '',
    },
    /**
     * 是否使用自定义标题样式
     * 启用后会使用自定义的标题栏样式，包括关闭按钮
     */
    customTitle: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否显示关闭按钮
     */
    showClose: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否显示底部区域
     */
    showFooter: {
      type: Boolean,
      default: true,
    },
    /**
     * 底部按钮对齐方式
     * @values 'left' | 'right'
     */
    footerAlign: {
      type: String,
      default: 'right',
      validator: value => ['left', 'right'].includes(value),
    },
    /**
     * 是否显示取消按钮
     */
    showCancelButton: {
      type: Boolean,
      default: true,
    },
    /**
     * 是否显示确认按钮
     */
    showConfirmButton: {
      type: Boolean,
      default: true,
    },
    /**
     * 取消按钮文本
     */
    cancelText: {
      type: String,
      default: '取消',
    },
    /**
     * 确认按钮文本
     */
    confirmText: {
      type: String,
      default: '确认',
    },
    /**
     * 按钮大小
     * @values 'medium' | 'small' | 'mini'
     */
    buttonSize: {
      type: String,
      default: 'medium',
      validator: value => ['medium', 'small', 'mini'].includes(value),
    },
    /**
     * 确认按钮加载状态
     */
    confirmLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * 取消按钮加载状态
     */
    cancelLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * 确认按钮禁用状态
     */
    confirmDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 取消按钮禁用状态
     */
    cancelDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 关闭前的回调函数
     * @param {Function} done 关闭对话框的回调函数
     */
    beforeClose: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      dialogVisible: this.visible,
    };
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
    },
    dialogVisible(val) {
      this.$emit('update:visible', val);
    },
  },
  methods: {
    /**
     * 处理对话框关闭事件
     * 如果设置了beforeClose，则调用beforeClose
     * 否则直接关闭对话框
     */
    handleClose() {
      if (typeof this.beforeClose === 'function') {
        this.beforeClose(() => {
          this.dialogVisible = false;
        });
      } else {
        this.dialogVisible = false;
      }
    },
    /**
     * 处理取消按钮点击事件
     * 触发cancel事件并关闭对话框
     */
    handleCancel() {
      this.$emit('cancel');
      this.handleClose();
    },
    /**
     * 处理确认按钮点击事件
     * 触发confirm事件
     */
    handleConfirm() {
      this.$emit('confirm');
    },
  },
};
</script>

<style lang="scss" scoped>
.rhcn-dialog-wrapper {
  :deep(.el-dialog) {
    border-radius: 18px;
    overflow: hidden;

    .el-dialog__header {
      padding: 24px 24px 17px;
      margin: 0;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        left: 24px;
        right: 24px;
        bottom: 0;
        height: 1px;
        background: #EFEFF4;
      }
    }

    .el-dialog__body {
      padding: 24px;
    }

    .el-dialog__footer {
      padding: 12px 24px 24px;
      position: relative;

      &::before {
        content: '';
        position: absolute;
        left: 24px;
        right: 24px;
        top: 0;
        height: 1px;
        background: #EFEFF4;
      }
    }
  }
}

.rhcn-dialog-title {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .title-text {
    font-family: Open Sans, Open Sans;
    font-weight: bold;
    font-size: 20px;
    color: #1A202C;
    line-height: 30px;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }

  .rhcn-dialog-close {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
}

.rhcn-dialog-footer {
  display: flex;
  justify-content: flex-start;
  gap: 12px;

  &.is-right {
    justify-content: flex-end;
  }

  :deep(.rhcn-button) {
    &.is-info {
      border-color: #D0D5DD;
      color: #1A202C;

      &:hover {
        border-color: #D0D5DD;
        color: #1A202C;
        background-color: #F9FAFB;
      }
    }
  }
}
</style>
